// Wire types for the publisher onboarding intake. Mirrors the API's
// IntakePayload (selfservice-api/src/modules/publisher-intake/intake-payload.ts).

export type IntakeTeamRole = 'sales_rep' | 'manager';

/**
 * CTV-803: devices a station or channel can stream on. Values are the
 * canonical strings stored in stations.devices (mirroring
 * STREAMING_DEVICE_VALUES in selfservice-api/src/shared/streaming-devices.ts);
 * labels are the UI copy. Picked by the super admin on the channel settings
 * form only; the intake stopped asking in CTV-855.
 */
export const STREAMING_DEVICES = [
  { value: 'roku', label: 'Roku' },
  { value: 'samsung_tv', label: 'Samsung TV' },
  { value: 'android_tv', label: 'Android TV' },
  { value: 'fire_tv', label: 'Fire TV' },
  { value: 'apple_tv', label: 'Apple TV' },
  { value: 'desktop', label: 'Desktop' },
] as const;

export type StreamingDevice = (typeof STREAMING_DEVICES)[number]['value'];

export interface IntakeTeamMember {
  name: string;
  email: string;
  role: IntakeTeamRole;
}

export interface IntakeClipSpec {
  storagePath: string;
  inSec?: number;
  outSec?: number;
}

export interface IntakeMarket {
  dmaCode: string;
  dmaName: string;
  coveredStates: string[];
}

export interface IntakeAdOpsContact {
  email: string;
  phone: string | null;
}

/** Notional commission visualizations: explicitly not billing data. */
export interface IntakeTeamCommission {
  enabled: boolean;
  defaultPercent: number | null;
}

/**
 * Everything the form v2 collects. Partial while a draft is in progress.
 *
 * Form v2 (CTV-680 revisions) dropped displayName, markets, minBudget and
 * minCampaignDays from the publisher form; those are admin-side at finalize
 * now. They survive on the WIRE type below because pre-v2 drafts and
 * submissions still carry them.
 */
export interface IntakeValues {
  slug: string;
  name: string;
  logoUrl: string;
  logoInvert: boolean;
  logoScale: number;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  ronCpm: string;
  backgroundClips: IntakeClipSpec[];
  teamMembers: IntakeTeamMember[];
  adOpsEmail: string;
  adOpsPhone: string;
  showTeamCommission: boolean;
  teamCommissionPercent: string;
}

/**
 * What `payload` actually looks like on the wire.
 *
 * An autosaved DRAFT round-trips the form's own string-typed inputs; a
 * SUBMITTED payload has been coerced to numbers by toIntakePayload. Both land
 * in the same jsonb column, so every reader has to handle either. Callers
 * coerce via hydrate() (form) or the adapter's num() (admin template).
 *
 * The pre-v2 fields (displayName, markets, minBudget, minCampaignDays) are
 * optional here: old rows carry them, new submissions do not.
 */
export interface IntakeWirePayload
  extends Omit<IntakeValues, 'ronCpm' | 'adOpsEmail' | 'adOpsPhone' | 'showTeamCommission' | 'teamCommissionPercent'> {
  ronCpm: string | number;
  /** Submitted shape (toIntakePayload nests). */
  adOpsContact?: { email?: string; phone?: string | null };
  /** Draft shape (autosave round-trips the form's flat inputs). */
  adOpsEmail?: string;
  adOpsPhone?: string;
  showTeamCommission?: boolean;
  teamCommissionPercent?: string;
  /** Submitted shape for the commission answers. */
  teamCommission?: { enabled?: boolean; defaultPercent?: number | null };
  displayName?: string | null;
  markets?: IntakeMarket[];
  minBudget?: string | number | null;
  minCampaignDays?: string | number | null;
}

export type PublisherIntakeStatus = 'draft' | 'submitted' | 'consumed' | 'revoked';

/** One member of a multi-station chain ("stations on this link"). */
export interface IntakeChainStation {
  name: string | null;
  slug: string | null;
  status: PublisherIntakeStatus;
  current: boolean;
  /** Null for consumed members — their token died at finalize. */
  token: string | null;
}

export interface PublisherIntakeView {
  intakeId: string;
  status: PublisherIntakeStatus;
  organizationName: string | null;
  contactName: string | null;
  expiresAt: string;
  submittedAt: string | null;
  payload: Partial<IntakeWirePayload>;
  /** Present on a consumed lookup: the chain, live members carrying tokens. */
  chain?: IntakeChainStation[];
}

export interface PublisherIntakeSummary {
  id: string;
  organizationId: string;
  organizationName: string | null;
  status: PublisherIntakeStatus;
  slug: string | null;
  stationName: string | null;
  contactName: string | null;
  contactEmail: string | null;
  submittedAt: string | null;
  expiresAt: string;
  consumedAt: string | null;
  consumedStationId: string | null;
}

/** Server-computed pre-flight signals for the admin loading a template. */
export type IntakeWarning =
  | 'slug_taken'
  | 'logo_missing'
  | 'clip_missing'
  | 'link_expired';

export interface PublisherIntakeSubmission extends PublisherIntakeSummary {
  payload: Partial<IntakeWirePayload>;
  warnings: IntakeWarning[];
  /**
   * Short-lived signed read URLs for the uploaded clips. The bucket is
   * private, so the timeline editor can't play them without these.
   */
  backgroundClipPreviews?: Array<{ storagePath: string; url: string }>;
}
